"use client";

import React, { useEffect, useRef, useState } from "react";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface StatData {
  prefix?: string;
  value: number;
  suffix?: string;
  label: string;
}
const statsData: StatData[] = [{
  value: 94,
  suffix: "%",
  label: "increased visibility within 90 days"
}, {
  value: 3,
  suffix: "X",
  label: "average increase in inbound inquiries"
}, {
  value: 100,
  suffix: "%",
  label: "would recommend us"
}];
function AnimatedCounter({
  value,
  prefix = "",
  suffix = "",
  duration = 2000
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true,
    threshold: 0.5
  });
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    let animationFrameId: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * value));
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      } else {
        setCount(value);
      }
    };
    animationFrameId = window.requestAnimationFrame(step);
    return () => {
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, [isVisible, value, duration]);
  return <span ref={ref} className="inline-block tabular-nums">
      {prefix}
      {count}
      {suffix}
    </span>;
}
export default function PrecisionInNumbers() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll<HTMLElement>({
    once: true,
    threshold: 0.1
  });
  return <section data-section-id="136245" id="proven-transformations" ref={sectionRef} className="bg-background text-foreground relative w-full overflow-hidden py-24 md:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20" style={{
      backgroundImage: 'radial-gradient(var(--primary) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className={`mx-auto max-w-5xl border border-primary/10 p-10 md:p-12 bg-card/50 backdrop-blur-sm shadow-xl shadow-foreground/5 transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-primary/10">
            {statsData.map((stat, i) => <div key={stat.label} data-index={i} className="flex flex-col items-center justify-center py-16 px-6 text-center group">
                {}
                <div className="font-serif text-5xl md:text-6xl lg:text-7xl font-normal tracking-tight text-accent transition-transform duration-500 group-hover:scale-105">
                  <AnimatedCounter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                </div>
                {}
                <div className="mt-6 text-sm font-sans font-medium uppercase tracking-widest text-primary">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}